import React, {useState} from "react";
import {isArrayNullOrEmpty} from "@utilities/Types/arrayUtilities";
import {GridTable} from "@components/Grid";
import {AphLoanRow} from "./AphLoanRow";
import {copyToClipboard, formatShippingAddress} from "./aphLoanUtility";
import {AphLoanEditFormView} from "./AphLoanEditFormView";
import {DialogBox} from "@components/Dialog/DialogBox";
import {AphLoanItem, AphLoanSearchCriteria} from "./aphLoan.types";
import {isNullOrUndefined} from "@utilities/commonUtilities";

interface AphLoanTableProps {
    aphLoanItems: AphLoanItem[];
    aphLoanSearchCriteria: AphLoanSearchCriteria;
    canManageAphLoans: boolean;
    handleSetAphLoanSearchCriteria: (criteria: AphLoanSearchCriteria) => void;
    handleSaveUpdatedAphLoanItem: (item: AphLoanItem) => void;
    updateAphLoanItemInState: (itemId: string, status: string | number, notes: string | null) => void;
}

export const AphLoanTable = ({
                                 aphLoanItems,
                                 aphLoanSearchCriteria,
                                 canManageAphLoans,
                                 handleSetAphLoanSearchCriteria,
                                 handleSaveUpdatedAphLoanItem,
                                 updateAphLoanItemInState,
                             }: AphLoanTableProps) => {

    const [aphLoanItemForDialog, setAphLoanItemForDialog] = useState<AphLoanItem | null>(null);
    const [isAphLoanEditDialogOpen, setIsAphLoanEditDialogOpen] = useState(false);

    aphLoanSearchCriteria?.ApplySorting && aphLoanSearchCriteria.ApplySorting(aphLoanSearchCriteria, aphLoanItems);

    const handleOrderClick = (event: React.MouseEvent<HTMLElement>) => {
        let criteria = {...aphLoanSearchCriteria};
        const columnId = (event.target as HTMLElement).dataset.id;
        if (columnId) criteria.UpdateSorting(columnId);
        criteria.loadSearchResults = false;
        handleSetAphLoanSearchCriteria(criteria);
    };


    const handleOpenEditDialog = (event: React.MouseEvent<HTMLElement>, aphLoanItem: AphLoanItem) => {
        event.preventDefault();

        setAphLoanItemForDialog(aphLoanItem);
        setIsAphLoanEditDialogOpen(true);
    }

    const handleCloseEditDialog = () => setIsAphLoanEditDialogOpen(false);


    const handleSaveAphLoanItem = () => {
        if (aphLoanItemForDialog) {
            handleSaveUpdatedAphLoanItem(aphLoanItemForDialog);
            handleCloseEditDialog();
        }
    };

    const handleUpdateAphLoanStatus = (aphLoanItemId: string, aphLoanStatus: string | number) => {
        updateAphLoanItemInState(aphLoanItemId, aphLoanStatus, null);
    }

    const handleCopyAddressToClipboard = async (aphLoanItem: AphLoanItem) => {
        const addressToCopy = formatShippingAddress(aphLoanItem.shippingLocation);
        await copyToClipboard(addressToCopy, "Address copied to clipboard.");
    };

    const handleCopyEmailToClipboard = async (aphLoanItem: AphLoanItem) => {
        const emailToCopy = isNullOrUndefined(aphLoanItem.patronEmail) ? 'No Email Found' : aphLoanItem.patronEmail;
        await copyToClipboard(emailToCopy, "Email copied to clipboard.");
    };


    const isLoanAphItemsEmpty = isArrayNullOrEmpty(aphLoanItems);
    const canOpenAphLoanItemEditDialog = canManageAphLoans && isAphLoanEditDialogOpen && aphLoanItemForDialog;

    return <>
        <GridTable className="searchResults" parentClass={`margin--topNone`}>
            <thead>
            <tr>
                <th onClick={handleOrderClick} className="is-clickable" data-id="dateCreatedUtc">Order Submitted
                    Date
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="aphCatalogItemTitle">Title</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="aphCatalogItemCost">Running Cost</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="patronFullName">Patron</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="districtName">District</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="organization">Organization</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="shippingLocationName">Shipping Location
                </th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="studentFullName">Student</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="aphCatalogNumber">Catalog Number</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="aphLoanStatusText">Status</th>
                <th onClick={handleOrderClick} className="is-clickable" data-id="aphLoanNotes">Notes</th>
                <th scope={"col"} className={"align-center"}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {
                !isLoanAphItemsEmpty &&
                aphLoanItems.map((aphLoanItem) => {
                    return <AphLoanRow
                        key={aphLoanItem.aphLoanItemId}
                        aphLoanItem={aphLoanItem}
                        canManageAphLoans={canManageAphLoans}
                        handleCopyAddressToClipboard={handleCopyAddressToClipboard}
                        handleOpenEditDialog={handleOpenEditDialog}
                        handleUpdateAphLoanStatus={handleUpdateAphLoanStatus}
                    />;
                })
            }
            {
                isLoanAphItemsEmpty &&
                <tr data-testid={`no-order-row`}>
                    <td colSpan={12} className={`text-center`}>No results found.</td>
                </tr>
            }
            </tbody>
        </GridTable>
        {
            canOpenAphLoanItemEditDialog &&
            <DialogBox
                handleCancel={handleCloseEditDialog}
                handleSave={handleSaveAphLoanItem}
                openDialog={isAphLoanEditDialogOpen}
                title={`Edit APH Loan Item: ${aphLoanItemForDialog.aphCatalogItemTitle}`}>
                <AphLoanEditFormView
                    aphLoanItemForDialog={aphLoanItemForDialog}
                    handleCopyAddressToClipboard={handleCopyAddressToClipboard}
                    handleCopyEmailToClipboard={handleCopyEmailToClipboard}
                    setAphLoanItemForDialog={setAphLoanItemForDialog}/>
            </DialogBox>
        }
    </>;
};
